import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCodeBranch, faExclamationTriangle, faPhone, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

const ClientOpenTicketsBanner = ({ openTickets = [], onOpenMergeModal, isMerging = false }) => {
  const context = useContext(AppContext);
  const isDark =
    context?.config?.isDark ??
    (typeof document !== 'undefined' &&
      (document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark')));

  if (!openTickets || openTickets.length === 0) return null;

  const count = openTickets.length;
  const firstTicket = openTickets[0];
  const firstTicketId = firstTicket?.idTicket || firstTicket?.id;

  return (
    <div
      className="px-3 py-2 border-bottom d-flex align-items-center justify-content-between flex-wrap gap-2"
      style={{
        backgroundColor: isDark ? 'rgba(245, 158, 11, 0.12)' : '#fef3c7',
        borderColor: isDark ? 'rgba(245, 158, 11, 0.25)' : '#fde68a'
      }}
    >
      <div className="d-flex align-items-center gap-2">
        <div
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '6px',
            backgroundColor: isDark ? 'rgba(245, 158, 11, 0.25)' : '#fde68a',
            color: isDark ? '#fbbf24' : '#b45309',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.85rem',
            flexShrink: 0
          }}
        >
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span
              className="fw-bold"
              style={{
                fontSize: '0.82rem',
                color: isDark ? '#fef3c7' : '#92400e'
              }}
            >
              {count === 1 ? (
                <>
                  Este cliente tiene otro ticket abierto:{' '}
                  <a
                    href={`/support-engine/ticket/${firstTicketId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="fw-bold text-decoration-underline d-inline-flex align-items-center gap-1"
                    style={{
                      color: isDark ? '#818cf8' : '#4f46e5',
                      cursor: 'pointer'
                    }}
                    title={`Abrir Ticket #${firstTicketId} en una nueva pestaña`}
                  >
                    #Ticket-{firstTicketId}
                    <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.65rem' }} />
                  </a>
                  {firstTicket?.vTelefono && (
                    <span
                      className="badge ms-2"
                      style={{
                        backgroundColor: isDark ? '#1e3a8a' : '#dbeafe',
                        color: isDark ? '#93c5fd' : '#1d4ed8',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        border: isDark ? '1px solid #2563eb' : '1px solid #bfdbfe'
                      }}
                      title="Número de origen del mensaje"
                    >
                      <FontAwesomeIcon icon={faPhone} className="me-1" style={{ fontSize: '0.65rem' }} />
                      {firstTicket.vTelefono}
                    </span>
                  )}
                  {firstTicket?.vAsunto ? ` — "${firstTicket.vAsunto}"` : ''}
                </>
              ) : (
                <>
                  Este cliente tiene <strong style={{ color: '#4f46e5' }}>{count} tickets abiertos</strong> adicionales en resolución:
                  <span className="d-inline-flex gap-1 ms-1 flex-wrap">
                    {openTickets.map(t => {
                      const tId = t.idTicket || t.id;
                      return (
                        <a
                          key={tId}
                          href={`/support-engine/ticket/${tId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="badge text-decoration-none d-inline-flex align-items-center"
                          style={{
                            backgroundColor: isDark ? '#1e3a8a' : '#dbeafe',
                            color: isDark ? '#93c5fd' : '#1d4ed8',
                            fontWeight: 600,
                            fontSize: '0.72rem',
                            border: isDark ? '1px solid #2563eb' : '1px solid #bfdbfe',
                            cursor: 'pointer'
                          }}
                          title={`Abrir Ticket #${tId} en una nueva pestaña`}
                        >
                          <FontAwesomeIcon icon={faPhone} className="me-1" style={{ fontSize: '0.6rem' }} />
                          #{tId} ({t.vTelefono})
                          <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-1" style={{ fontSize: '0.55rem' }} />
                        </a>
                      );
                    })}
                  </span>
                </>
              )}
            </span>
          </div>
          <span
            className="d-block fs--2"
            style={{ color: isDark ? '#d1d5db' : '#78350f' }}
          >
            Puedes combinarlos para unificar el historial de mensajes y evitar respuestas duplicadas.
          </span>
        </div>
      </div>

      <Button
        variant="primary"
        size="sm"
        disabled={isMerging}
        onClick={onOpenMergeModal}
        className="d-flex align-items-center gap-1 shadow-sm"
        style={{
          backgroundColor: '#4f46e5',
          borderColor: '#4f46e5',
          fontSize: '0.78rem',
          padding: '4px 12px',
          fontWeight: 600
        }}
      >
        <FontAwesomeIcon icon={faCodeBranch} style={{ fontSize: '0.75rem' }} />
        <span>
          {count === 1 ? `Combinar con Ticket #${firstTicketId}` : `Ver y Combinar (${count})`}
        </span>
      </Button>
    </div>
  );
};

ClientOpenTicketsBanner.propTypes = {
  openTickets: PropTypes.array,
  onOpenMergeModal: PropTypes.func.isRequired,
  isMerging: PropTypes.bool
};

export default ClientOpenTicketsBanner;
